import React, { useState, useCallback } from "react";
import { RefreshCw, ExternalLink, Calculator } from "lucide-react";
import GoogleSheetsTable from "./GoogleSheetsTable";
import { GoogleSheetsService } from "../services/googleSheetsService";
import { AlphaPickService } from "../services/alphaPickService";
import { useConfigStore } from "../store/configStore";
import Select from "./ui/Select";
import { buttonClass } from './ui/Button';

interface RatingOption {
  value: string;
  label: string;
}

const btnClass =
  buttonClass('secondary', 'sm');

const AlphaPicks: React.FC = () => {
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [ratingFilter, setRatingFilter] = useState("");
  const [ratingOptions, setRatingOptions] = useState<RatingOption[]>([
    { value: "", label: "All Ratings" },
  ]);

  const { getConfigValue } = useConfigStore();
  const googleSheetsUrl = getConfigValue(
    "google_sheets_url",
    "https://docs.google.com/spreadsheets/d/1NH5JOSkN5fbnbUwaUEaAN80Qcfaki03NpiT8R6EOjj0",
  );
  const shayPortfolioUrl = getConfigValue(
    "shay_portfolio_url",
    "https://docs.google.com/spreadsheets/d/1vCkxvFGKYDeLag5k2WSIjSFR0Zl2nuUi9cye6leOf5g",
  );

  const googleSheetsService = new GoogleSheetsService(googleSheetsUrl);
  const alphaPickService = new AlphaPickService();

  const handleRatingOptionsChange = useCallback((opts: RatingOption[]) => {
    setRatingOptions(opts);
  }, []);

  const handleReload = async () => {
    setLoading(true);
    try {
      const stockData = await googleSheetsService.fetchData();
      const alphaPicks =
        alphaPickService.transformStockDataToAlphaPicks(stockData);
      await alphaPickService.saveAlphaPicks(alphaPicks);
      await alphaPickService.calculateReturns();
      setReloadKey((prev) => prev + 1);
    } catch (error) {
      console.error("❌ Error during reload:", error);
      alert(
        `Error during reload: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateReturns = async () => {
    setCalculating(true);
    try {
      await alphaPickService.calculateReturns();
      setReloadKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error calculating returns:", error);
      alert(
        `Error calculating returns: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setCalculating(false);
    }
  };

  return (
    <>
      <div className="p-6 border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-shrink-0"></div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="w-36">
            <Select
              value={ratingFilter}
              options={ratingOptions}
              onChange={setRatingFilter}
              size="sm"
              placeholder="All Ratings"
            />
          </div>
          <a
            href={googleSheetsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={btnClass}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Alpha Picks
          </a>
          <a
            href={shayPortfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={btnClass}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Shay Portfolio
          </a>
          <button
            onClick={handleCalculateReturns}
            disabled={calculating || loading}
            className={btnClass}
          >
            <Calculator
              className={`w-4 h-4 mr-2 ${calculating ? "animate-pulse" : ""}`}
            />
            {calculating ? "Calculating..." : "Calculate Returns"}
          </button>
          <button
            onClick={handleReload}
            disabled={loading || calculating}
            className={btnClass}
            title="Reload"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      <div className="pb-6">
        <GoogleSheetsTable
          reloadKey={reloadKey}
          ratingFilter={ratingFilter}
          onRatingOptionsChange={handleRatingOptionsChange}
        />
      </div>
    </>
  );
};

export default AlphaPicks;
